import {
  Injectable,
  inject,
  signal,
  computed,
  DestroyRef,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * FE-stream replay service (GAP-FU-11).
 *
 * Buffers recent events from any RxJS stream and re-emits them through
 * a scrubbable timeline. No backend involvement — per user decision in
 * plan §5, replay is purely FE.
 *
 * Producers call `record(event)` continuously while the user watches
 * live. Consumers read `currentEvents()` which yields:
 *   • the live tail in normal mode, or
 *   • whatever the scrubber is pointing at when `isReplaying` is true.
 *
 * The bar component (ReplayBarComponent) is the single UI mounted by the
 * trading layout to drive the scrubber. Multiple producers can record;
 * the service stores events in a single time-ordered buffer.
 */
export interface ReplayEvent<T = unknown> {
  /** Unix-ms timestamp. */
  readonly t: number;
  /** Free-form payload. The consumer knows the shape per producer-id. */
  readonly payload: T;
  /** Producer label, e.g. 'order-book', 'fill', 'risk-alert'. */
  readonly source: string;
}

const DEFAULT_WINDOW_MS = 60 * 60 * 1000;  // 60 minutes

@Injectable({ providedIn: 'root' })
export class ReplayService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  /** Rolling buffer; oldest events fall out as the window slides. */
  private readonly buffer = signal<ReplayEvent[]>([]);

  /** Window size in ms (60 min default). */
  private readonly windowMs = signal<number>(DEFAULT_WINDOW_MS);

  /** Playback state. */
  private readonly _isReplaying = signal<boolean>(false);
  private readonly _scrubberMs = signal<number>(Date.now());
  private readonly _speed = signal<number>(1);

  readonly isReplaying = this._isReplaying.asReadonly();
  readonly scrubberMs = this._scrubberMs.asReadonly();
  readonly speed = this._speed.asReadonly();

  /**
   * Whether replay MODE is active — i.e. the scrubber bar is revealed. Distinct
   * from `isReplaying` (playback actively ticking). Off by default: the bar
   * stays hidden until the operator explicitly enters replay mode
   * (⌘K → "Enter replay mode"); `exitReplayMode()` returns to live.
   */
  private readonly _active = signal<boolean>(false);
  readonly active = this._active.asReadonly();

  readonly events = this.buffer.asReadonly();

  /** Oldest timestamp in the buffer (for the scrubber's min). */
  readonly minMs = computed(() => {
    const buf = this.buffer();
    return buf.length ? buf[0].t : Date.now() - this.windowMs();
  });

  /** Newest timestamp (for the scrubber's max). */
  readonly maxMs = computed(() => Date.now());

  /** Currently-visible events — slice up to scrubber when replaying. */
  readonly currentEvents = computed<readonly ReplayEvent[]>(() => {
    const buf = this.buffer();
    if (!this._isReplaying()) return buf;
    const cutoff = this._scrubberMs();
    return buf.filter(e => e.t <= cutoff);
  });

  private playbackTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.stopPlayback());
  }

  /** Record an event into the rolling buffer. */
  record(event: Omit<ReplayEvent, 't'> & { t?: number }): void {
    const ts = event.t ?? Date.now();
    const cutoff = ts - this.windowMs();
    this.buffer.update(buf => {
      // Drop events outside the window, append new.
      const filtered = buf.filter(e => e.t >= cutoff);
      return [...filtered, { t: ts, source: event.source, payload: event.payload }];
    });
  }

  setWindowMs(ms: number): void {
    this.windowMs.set(ms);
  }

  startReplay(atMs?: number): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const buf = this.buffer();
    const min = buf.length ? buf[0].t : Date.now() - this.windowMs();
    this._scrubberMs.set(atMs ?? min);
    this._isReplaying.set(true);
    this.tickPlayback();
  }

  stopReplay(): void {
    this._isReplaying.set(false);
    this.stopPlayback();
  }

  /**
   * Enter replay mode — reveal the scrubber bar over the buffered events. Does
   * NOT auto-start playback; the operator then scrubs or presses play.
   */
  enterReplayMode(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this._active.set(true);
  }

  /**
   * Exit replay mode — stop any playback and hide the scrubber bar, returning
   * the UI to the live tail.
   */
  exitReplayMode(): void {
    this.stopReplay();
    this._active.set(false);
  }

  setScrubber(ms: number): void {
    this._scrubberMs.set(ms);
  }

  setSpeed(speed: number): void {
    this._speed.set(speed);
    if (this._isReplaying()) {
      this.stopPlayback();
      this.tickPlayback();
    }
  }

  private tickPlayback(): void {
    this.stopPlayback();
    this.playbackTimer = setInterval(() => {
      const next = this._scrubberMs() + 1000 * this._speed();
      if (next >= this.maxMs()) {
        this.stopReplay();
        return;
      }
      this._scrubberMs.set(next);
    }, 1000);
  }

  private stopPlayback(): void {
    if (this.playbackTimer) {
      clearInterval(this.playbackTimer);
      this.playbackTimer = null;
    }
  }
}
